import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import BaseModal from '../../_shared/components/BaseModal';
import theme from '../../_shared/themes';
import {
  CheckBox,
  BoldText,
  StyledDescription,
  StyledLabel,
  ModalOuterBox,
  ModalOuterBoxContainer,
  Divider,
  ModalContentWrapper,
  ModalSaveButtonWrapper,
  ModalSaveButton,
  StyledUl,
  StyledLi
} from './Styles';
import SettingsService from '../../services/SettingsService';
import Alert from '../../_shared/components/Alert';
import { httpCodes } from '../../_shared/constants/httpMethods';
import { ModalCloseButton } from '../SearchModal/Styles';
import TRToast from '../../_shared/helpers/TRToast';

const propTypes = {
  isModalOpen: PropTypes.bool
};

const defaultProps = {
  isModalOpen: false
};

const AutoWinSettingModal = ({ testId, isModalOpen, closeHandler }) => {
  const [autoWinEnabled, setAutoWinEnabled] = useState();

  const settingsService = SettingsService.get();

  const autoWinCheckBoxRef = useRef(null);

  useEffect(() => {
    const getAutoWinSettings = async () => {
      const autoWinEnabledResponse = await settingsService.getAutoWinSetting();
      setAutoWinEnabled(autoWinEnabledResponse);
    };

    getAutoWinSettings();
  }, [settingsService]);

  const handleSave = () => {
    const autoWinChecked = autoWinCheckBoxRef?.current?.checked;
    const updateAutoWinSettings = async () => {
      const updateResponse = await settingsService.updateAutoWinSetting(
        autoWinChecked
      );
      if (updateResponse?.status === httpCodes.OK) {
        setAutoWinEnabled(autoWinChecked);
        const messageId = autoWinChecked
          ? 'autowin.setting.enabled'
          : 'autowin.setting.disabled';
        TRToast(
          <Alert
            variant="success"
            messages={[
              <FormattedMessage
                id={messageId}
                data-testid={`${testId}-message-toast`}
              />
            ]}
          />,
          {
            onClose: closeHandler
          }
        );
      }
    };

    if (autoWinEnabled !== autoWinChecked) {
      updateAutoWinSettings();
    }
  };

  return (
    <BaseModal
      isOpen={isModalOpen}
      isFullScreen
      padding="0"
      bodyOpenClassName="ReactModal__FullScreen"
      backgroundColor={theme.COLORS.NEUTRAL_60}
      isClickEscClose
      onRequestClose={closeHandler}
    >
      <ModalCloseButton
        onClick={closeHandler}
        data-testid={`${testId}-closeButton`}
      >
        <FormattedMessage id="list.nav.searchbar.modal.closeButton" />
        <ModalCloseButton.Icon />
      </ModalCloseButton>
      <ModalOuterBoxContainer>
        <ModalOuterBox>
          <ModalContentWrapper>
            <BoldText data-testid={`${testId}-bidSettings`}>
              <FormattedMessage id="autowin.setting.bid.settings" />
            </BoldText>
            <Divider />
            <BoldText data-testid={`${testId}-autoWin`}>
              <FormattedMessage id="autowin.setting.auto.win" />
            </BoldText>
            <StyledDescription data-testid={`${testId}-description`}>
              <FormattedMessage id="autowin.setting.description" />
            </StyledDescription>
            <StyledDescription data-testid={`${testId}-floorPrice`}>
              <FormattedMessage id="autowin.setting.unmet.floor.price.ends" />
              <StyledUl>
                <StyledLi data-testid={`${testId}-proxyBid`}>
                  <FormattedMessage id="autowin.setting.proxy.bid" />
                </StyledLi>
              </StyledUl>
            </StyledDescription>
            <StyledDescription data-testid={`${testId}-negotiation`}>
              <FormattedMessage id="autowin.setting.negotiation" />
              <StyledUl>
                <StyledLi data-testid={`${testId}-firstOffer`}>
                  <FormattedMessage id="autowin.setting.seller.first.offer" />
                </StyledLi>
              </StyledUl>
            </StyledDescription>
            <StyledLabel>
              <CheckBox
                defaultChecked={autoWinEnabled}
                data-testid={`${testId}-checkbox`}
                ref={autoWinCheckBoxRef}
              />
              <FormattedMessage id="autowin.setting.enable.auto.win" />
            </StyledLabel>
            <ModalSaveButtonWrapper>
              <ModalSaveButton
                data-testid={`${testId}-saveButton`}
                onClick={handleSave}
              >
                <FormattedMessage
                  id="autowin.setting.save"
                  data-testid={`${testId}-save`}
                />
              </ModalSaveButton>
            </ModalSaveButtonWrapper>
          </ModalContentWrapper>
        </ModalOuterBox>
      </ModalOuterBoxContainer>
    </BaseModal>
  );
};

AutoWinSettingModal.propTypes = propTypes;
AutoWinSettingModal.defaultProps = defaultProps;

export default AutoWinSettingModal;
